import React, { useState, useEffect } from "react";
import { apiFetch } from "../../api/apiClient";

import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Paper,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CodeIcon from "@mui/icons-material/Code";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

const API_BASE = "/api/admin";

const DEFAULT_JSON_TEMPLATE = {
  basePrompt: "Create a {styleDescriptor} image of {productName}",
  styleDescriptors: {
    professional: "professional product photography with studio lighting",
    lifestyle: "lifestyle product shot in natural setting",
    minimalist: "clean minimalist product image with simple background",
    vibrant: "vibrant and colorful product photography",
  },
  packagingInstructions: {
    "Flexible Pouch": "showing flexible pouch packaging with clear product visibility",
    "Rigid Box": "displaying rigid box packaging with premium appearance",
    Bottle: "featuring bottle with clear labeling",
    Can: "showcasing can with brand graphics",
  },
  sizeContext: {
    template: "The package is standard retail size ({skuWt} {skuWtUnit})",
  },
  positioningAttributes: {
    Premium: "with premium quality appearance and elegant presentation",
    "Mid-Range": "with balanced quality and affordability appearance",
    Economy: "with simple and cost-effective presentation",
  },
  brandingTemplate: {
    both: "Brand name '{brandName}' by {companyName} should be clearly visible",
    brandOnly: "Brand name '{brandName}' should be prominently displayed",
    companyOnly: "Company name '{companyName}' should be clearly visible",
  },
  finalInstructions: [
    "Use {angle} camera angle",
    "Set against {background} background",
    "Ensure professional lighting and sharp focus",
    "Make the product the central focus of the image",
  ],
  brandingReminder: "Ensure brand identity is clearly visible and professionally presented",
};

const DALL_E_MODELS = [
  { value: "dall-e-2", label: "DALL-E 2" },
  { value: "dall-e-3", label: "DALL-E 3" },
];

const IMAGE_SIZES = {
  "dall-e-2": ["256x256", "512x512", "1024x1024"],
  "dall-e-3": ["1024x1024", "1024x1792", "1792x1024"],
};

const QUALITY_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "hd", label: "HD (DALL-E 3 only)" },
];

const ImageSchemaManagementV2 = ({ selectedTower, selectedCommodity }) => {
  // State management
  const [imageSchemas, setImageSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Tab state
  const [editorTab, setEditorTab] = useState(0); // 0=Model Config, 1=Input Schema
  const [activeTab, setActiveTab] = useState(0); // 0=Versions, 1=Audit Log
  const [schemaViewMode, setSchemaViewMode] = useState("builder"); // "builder" or "json"
  const [expandedFields, setExpandedFields] = useState({});
  const [selectedField, setSelectedField] = useState(null);

  // Field editor dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFieldPath, setEditingFieldPath] = useState("");
  const [editingFieldKey, setEditingFieldKey] = useState("");
  const [editingFieldValue, setEditingFieldValue] = useState("");
  const [editingFieldType, setEditingFieldType] = useState("string");

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);

  // Editor state
  const [promptTemplate, setPromptTemplate] = useState(
    JSON.stringify(DEFAULT_JSON_TEMPLATE, null, 2)
  );
  const [model, setModel] = useState("dall-e-3");
  const [size, setSize] = useState("1024x1024");
  const [quality, setQuality] = useState("standard");
  const [description, setDescription] = useState("");

  // Dialog state
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [preview, setPreview] = useState("");

  // Load schemas
  useEffect(() => {
    if (selectedTower && selectedCommodity) {
      fetchImageSchemas();
    }
  }, [selectedTower, selectedCommodity]);

  // Generate preview
  useEffect(() => {
    generatePreview();
  }, [promptTemplate]);

  const generatePreview = () => {
    const sampleValues = {
      productName: "Chocolate Chip Cookies",
      companyName: "Acme Foods",
      brandName: "Delicious",
      primaryPackaging: "Flexible Pouch",
      skuWt: "200",
      skuWtUnit: "g",
      marketPositioning: "Premium",
      angle: "45-degree",
      background: "clean white studio",
    };

    try {
      const jsonTemplate = JSON.parse(promptTemplate);
      let previewParts = [];

      const style = "professional";
      const styleDesc =
        jsonTemplate.styleDescriptors?.[style] || "Professional product photography";
      let base = jsonTemplate.basePrompt || "{styleDescriptor} of {productName}";
      base = base.replace("{styleDescriptor}", styleDesc);
      base = base.replace("{productName}", sampleValues.productName);
      previewParts.push(base);

      const packagingDesc =
        jsonTemplate.packagingInstructions?.[sampleValues.primaryPackaging] || "";
      if (packagingDesc) previewParts.push(packagingDesc);

      if (jsonTemplate.sizeContext?.template) {
        let sizeText = jsonTemplate.sizeContext.template;
        sizeText = sizeText.replace("{skuWt}", sampleValues.skuWt);
        sizeText = sizeText.replace("{skuWtUnit}", sampleValues.skuWtUnit);
        previewParts.push(sizeText);
      }

      const positioning =
        jsonTemplate.positioningAttributes?.[sampleValues.marketPositioning] || "";
      if (positioning) previewParts.push(positioning);

      const branding = jsonTemplate.brandingTemplate?.both || "";
      if (branding) {
        let brandText = branding.replace("{brandName}", sampleValues.brandName);
        brandText = brandText.replace("{companyName}", sampleValues.companyName);
        previewParts.push(brandText);
      }

      const finalInstructions = jsonTemplate.finalInstructions || [];
      const processedInstructions = finalInstructions.map((inst) => {
        let text = inst;
        text = text.replace("{angle}", sampleValues.angle);
        text = text.replace("{background}", sampleValues.background);
        return text;
      });

      if (jsonTemplate.brandingReminder) {
        processedInstructions.push(jsonTemplate.brandingReminder);
      }

      const fullPreview = previewParts.join(". ") + ". " + processedInstructions.join(". ") + ".";
      setPreview(fullPreview);
    } catch (e) {
      setPreview("Invalid JSON - fix syntax to see preview");
    }
  };

  const fetchImageSchemas = async () => {
    if (!selectedTower || !selectedCommodity) return;

    try {
      setLoading(true);
      const response = await apiFetch(
        `${API_BASE}/image-schemas?tower=${selectedTower}&commodity=${selectedCommodity}`
      );
      const data = await response.json();
      setImageSchemas(data.versions || []);

      const activeSchema = (data.versions || []).find((s) => s.is_active);
      if (activeSchema) {
        loadSchema(activeSchema.id);
      } else if (data.versions?.length > 0) {
        loadSchema(data.versions[0].id);
      } else {
        // No schemas found - set up default state for new schema creation
        setSelectedSchema({
          id: null,
          version: 1,
          tower: selectedTower,
          commodity: selectedCommodity,
          is_active: false,
        });
        setPromptTemplate(JSON.stringify(DEFAULT_JSON_TEMPLATE, null, 2));
        setModel("dall-e-3");
        setSize("1024x1024");
        setQuality("standard");
        setDescription("");
        setHasUnsavedChanges(true); // Enable save button for new schema
      }
    } catch (error) {
      setError("Failed to load image schemas");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSchema = async (schemaId) => {
    try {
      setLoading(true);
      const response = await apiFetch(`${API_BASE}/image-schemas/${schemaId}`);
      const data = await response.json();

      setSelectedSchema(data);
      setPromptTemplate(data.prompt_template || JSON.stringify(DEFAULT_JSON_TEMPLATE, null, 2));
      setModel(data.model || "dall-e-3");
      setSize(data.size || "1024x1024");
      setQuality(data.quality || "standard");
      setDescription(data.description || "");
      setHasUnsavedChanges(false);
    } catch (error) {
      setError("Failed to load schema details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePromptChange = (value) => {
    setPromptTemplate(value || "");
    setHasUnsavedChanges(true);
  };

  // Move field up or down in the template
  const handleMoveField = (path, direction) => {
    try {
      const template = JSON.parse(promptTemplate);
      const pathParts = path.split(".");
      const fieldKey = pathParts[pathParts.length - 1];

      // Get parent object
      let parent = template;
      for (let i = 0; i < pathParts.length - 1; i++) {
        parent = parent[pathParts[i]];
      }

      const keys = Object.keys(parent);
      const currentIndex = keys.indexOf(fieldKey);

      if (direction === "up" && currentIndex > 0) {
        const newIndex = currentIndex - 1;
        const reorderedKeys = [...keys];
        [reorderedKeys[currentIndex], reorderedKeys[newIndex]] = [
          reorderedKeys[newIndex],
          reorderedKeys[currentIndex],
        ];

        const newParent = {};
        reorderedKeys.forEach((key) => {
          newParent[key] = parent[key];
        });

        // Update parent
        if (pathParts.length === 1) {
          Object.keys(template).forEach((k) => delete template[k]);
          Object.assign(template, newParent);
        } else {
          let target = template;
          for (let i = 0; i < pathParts.length - 2; i++) {
            target = target[pathParts[i]];
          }
          target[pathParts[pathParts.length - 2]] = newParent;
        }
      } else if (direction === "down" && currentIndex < keys.length - 1) {
        const newIndex = currentIndex + 1;
        const reorderedKeys = [...keys];
        [reorderedKeys[currentIndex], reorderedKeys[newIndex]] = [
          reorderedKeys[newIndex],
          reorderedKeys[currentIndex],
        ];

        const newParent = {};
        reorderedKeys.forEach((key) => {
          newParent[key] = parent[key];
        });

        // Update parent
        if (pathParts.length === 1) {
          Object.keys(template).forEach((k) => delete template[k]);
          Object.assign(template, newParent);
        } else {
          let target = template;
          for (let i = 0; i < pathParts.length - 2; i++) {
            target = target[pathParts[i]];
          }
          target[pathParts[pathParts.length - 2]] = newParent;
        }
      }

      setPromptTemplate(JSON.stringify(template, null, 2));
      setHasUnsavedChanges(true);
    } catch (e) {
      setError("Failed to move field: " + e.message);
    }
  };

  // Delete field from template
  const handleDeleteField = (path) => {
    try {
      const template = JSON.parse(promptTemplate);
      const pathParts = path.split(".");
      const fieldKey = pathParts[pathParts.length - 1];

      // Get parent object
      let parent = template;
      for (let i = 0; i < pathParts.length - 1; i++) {
        parent = parent[pathParts[i]];
      }

      delete parent[fieldKey];

      setPromptTemplate(JSON.stringify(template, null, 2));
      setHasUnsavedChanges(true);
      setSelectedField(null);
      setSuccess(`Deleted field: ${fieldKey}`);
    } catch (e) {
      setError("Failed to delete field: " + e.message);
    }
  };

  // Open edit dialog for a field
  const handleOpenEditDialog = (path, key, value) => {
    setEditingFieldPath(path);
    setEditingFieldKey(key);

    const valueType = Array.isArray(value)
      ? "array"
      : typeof value === "object" && value !== null
      ? "object"
      : typeof value;
    setEditingFieldType(valueType);

    if (valueType === "object" || valueType === "array") {
      setEditingFieldValue(JSON.stringify(value, null, 2));
    } else {
      setEditingFieldValue(String(value));
    }

    setEditDialogOpen(true);
  };

  // Save edited field
  const handleSaveEditedField = () => {
    try {
      const template = JSON.parse(promptTemplate);
      const pathParts = editingFieldPath.split(".");

      // Navigate to parent
      let parent = template;
      for (let i = 0; i < pathParts.length - 1; i++) {
        parent = parent[pathParts[i]];
      }

      // Parse and set the new value
      let newValue;
      if (editingFieldType === "object" || editingFieldType === "array") {
        newValue = JSON.parse(editingFieldValue);
      } else if (editingFieldType === "number") {
        newValue = Number(editingFieldValue);
      } else if (editingFieldType === "boolean") {
        newValue = editingFieldValue === "true" || editingFieldValue === true;
      } else {
        newValue = editingFieldValue;
      }

      parent[editingFieldKey] = newValue;

      setPromptTemplate(JSON.stringify(template, null, 2));
      setHasUnsavedChanges(true);
      setEditDialogOpen(false);
      setSuccess(`Updated field: ${editingFieldKey}`);
    } catch (e) {
      setError("Failed to save field: " + e.message);
    }
  };

  const handleSaveNewVersion = async () => {
    if (!selectedTower || !selectedCommodity) {
      setError("Please select tower and commodity");
      return;
    }

    try {
      JSON.parse(promptTemplate);
    } catch (e) {
      setError("Invalid JSON in prompt template");
      return;
    }

    try {
      setSaving(true);

      // If editing existing schema, update it; otherwise create new
      if (selectedSchema?.id) {
        // Update existing schema
        const response = await apiFetch(`${API_BASE}/image-schemas/${selectedSchema.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prompt_template: promptTemplate,
            model,
            size,
            quality,
            description,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Server error: ${response.status}`);
        }

        setSuccess("Schema updated successfully");
      } else {
        // Create new schema version
        const response = await apiFetch(`${API_BASE}/image-schemas`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tower: selectedTower,
            commodity: selectedCommodity,
            prompt_template: promptTemplate,
            model,
            size,
            quality,
            description,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Server error: ${response.status}`);
        }

        setSuccess("New schema version created successfully");
      }

      setHasUnsavedChanges(false);
      await fetchImageSchemas();
    } catch (error) {
      setError(`Failed to save: ${error.message}`);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleActivateSchema = async (schemaId) => {
    try {
      const response = await apiFetch(`${API_BASE}/image-schemas/${schemaId}/activate`, {
        method: "POST",
      });

      if (!response.ok) throw new Error("Failed to activate");

      setSuccess("Schema activated successfully");
      await fetchImageSchemas();
    } catch (error) {
      setError("Failed to activate schema");
      console.error(error);
    }
  };

  const handleDeleteSchema = async (schemaId) => {
    if (!confirm("Are you sure you want to delete this schema version?")) return;

    try {
      const response = await apiFetch(`${API_BASE}/image-schemas/${schemaId}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete");

      setSuccess("Schema deleted successfully");
      await fetchImageSchemas();
    } catch (error) {
      setError("Failed to delete schema");
      console.error(error);
    }
  };

  const getSchemaStructure = () => {
    try {
      const parsed = JSON.parse(promptTemplate);
      return [
        { key: "basePrompt", type: "string", value: parsed.basePrompt },
        {
          key: "styleDescriptors",
          type: "object",
          count: Object.keys(parsed.styleDescriptors || {}).length,
        },
        {
          key: "packagingInstructions",
          type: "object",
          count: Object.keys(parsed.packagingInstructions || {}).length,
        },
        { key: "sizeContext", type: "object", value: parsed.sizeContext?.template },
        {
          key: "positioningAttributes",
          type: "object",
          count: Object.keys(parsed.positioningAttributes || {}).length,
        },
        {
          key: "brandingTemplate",
          type: "object",
          count: Object.keys(parsed.brandingTemplate || {}).length,
        },
        {
          key: "finalInstructions",
          type: "array",
          count: (parsed.finalInstructions || []).length,
        },
        { key: "brandingReminder", type: "string", value: parsed.brandingReminder },
      ];
    } catch (e) {
      return [];
    }
  };

  if (!selectedTower || !selectedCommodity) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Select a Tower and Commodity to manage image schemas
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        flex: 1,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mx: 2, mt: 1 }}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      {selectedSchema && (
        <Box
          sx={{
            display: "flex",
            gap: 0,
            flex: 1,
            overflow: "hidden",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          {/* Editor Section - Full Height - Fixed Width */}
          <Box
            sx={{
              flex: "0 0 70%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              borderRight: 1,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            {/* Editor Header with Tabs */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                backgroundColor: "background.paper",
                flexShrink: 0,
              }}
            >
              {/* Tabs for Model Config and Input Schema */}
              <Tabs
                value={editorTab}
                onChange={(e, v) => setEditorTab(v)}
                sx={{ px: 2 }}
                variant="standard"
              >
                <Tab label="Model Config" />
                <Tab label="JSON Prompt" />
              </Tabs>
            </Box>

            {/* Tabbed Editor Content */}
            <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
              {/* Tab 0: Model Configuration */}
              {editorTab === 0 && (
                <Box sx={{ height: "100%", overflow: "auto", p: 3 }}>
                  <Paper sx={{ p: 3, maxWidth: 600 }}>
                    <Typography variant="h6" gutterBottom>
                      Image Generation Model Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Configure the DALL-E model settings for this schema
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Model</InputLabel>
                      <Select
                        value={model}
                        label="Model"
                        onChange={(e) => {
                          setModel(e.target.value);
                          if (!IMAGE_SIZES[e.target.value].includes(size)) {
                            setSize(IMAGE_SIZES[e.target.value][0]);
                          }
                          setHasUnsavedChanges(true);
                        }}
                      >
                        {DALL_E_MODELS.map((m) => (
                          <MenuItem key={m.value} value={m.value}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Image Size</InputLabel>
                      <Select
                        value={size}
                        label="Image Size"
                        onChange={(e) => {
                          setSize(e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                      >
                        {IMAGE_SIZES[model].map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Quality</InputLabel>
                      <Select
                        value={quality}
                        label="Quality"
                        onChange={(e) => {
                          setQuality(e.target.value);
                          setHasUnsavedChanges(true);
                        }}
                        disabled={model === "dall-e-2"}
                      >
                        {QUALITY_OPTIONS.map((q) => (
                          <MenuItem
                            key={q.value}
                            value={q.value}
                            disabled={q.value === "hd" && model === "dall-e-2"}
                          >
                            {q.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Description"
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Brief description of this schema version"
                      multiline
                      rows={3}
                    />

                    {hasUnsavedChanges && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        You have unsaved changes. Don't forget to save as a new version.
                      </Alert>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Tab 1: JSON Prompt (JSON Prompt Template) */}
              {editorTab === 1 && (
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "background.default",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        JSON Prompt
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        JSON template structure for AI image generation prompts
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {/* View Mode Toggle */}
                      <ToggleButtonGroup
                        value={schemaViewMode}
                        exclusive
                        onChange={(e, newMode) => {
                          if (newMode) setSchemaViewMode(newMode);
                        }}
                        size="small"
                        sx={{
                          "& .MuiToggleButton-root": {
                            px: 1.5,
                            py: 0.5,
                            fontSize: "0.75rem",
                            textTransform: "none",
                          },
                        }}
                      >
                        <ToggleButton value="builder">
                          <AccountTreeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          Builder
                        </ToggleButton>
                        <ToggleButton value="json">
                          <CodeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          JSON
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <Tooltip title="Validate JSON">
                        <IconButton
                          size="small"
                          onClick={() => {
                            try {
                              JSON.parse(promptTemplate);
                              setSuccess("JSON is valid!");
                            } catch (e) {
                              setError("Invalid JSON: " + e.message);
                            }
                          }}
                          sx={{
                            color: "white",
                          }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={selectedSchema?.id ? "Save Changes" : "Save as New Version"}>
                        <IconButton
                          size="small"
                          onClick={handleSaveNewVersion}
                          disabled={!hasUnsavedChanges || saving}
                          sx={{
                            color: hasUnsavedChanges ? "white" : "text.secondary",
                            "&.Mui-disabled": {
                              color: "action.disabled",
                            },
                          }}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Schema Builder Mode */}
                  {schemaViewMode === "builder" && (
                    <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                      {(() => {
                        try {
                          const template = JSON.parse(promptTemplate);

                          // Recursive function to render template fields
                          const renderTemplateField = (key, value, depth = 0, path = "") => {
                            const fullPath = path ? `${path}.${key}` : key;
                            const isExpanded = expandedFields[fullPath] !== false; // default expanded
                            const isObject =
                              typeof value === "object" && value !== null && !Array.isArray(value);
                            const isArray = Array.isArray(value);

                            let fieldType = "string";
                            let fieldCount = 0;

                            if (isObject) {
                              fieldType = "object";
                              fieldCount = Object.keys(value).length;
                            } else if (isArray) {
                              fieldType = "array";
                              fieldCount = value.length;
                            }

                            return (
                              <Box key={fullPath}>
                                <Paper
                                  elevation={2}
                                  onClick={() => setSelectedField(fullPath)}
                                  sx={{
                                    p: 1,
                                    mb: 1,
                                    ml: depth * 3,
                                    minHeight: "48px",
                                    border: 2,
                                    borderColor:
                                      selectedField === fullPath ? "primary.dark" : "divider",
                                    backgroundColor:
                                      selectedField === fullPath ? "#191A1B" : "background.paper",
                                    "&:hover": {
                                      boxShadow: 4,
                                      cursor: "pointer",
                                      borderColor: "primary.light",
                                    },
                                    transition: "all 0.2s ease-in-out",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      width: "100%",
                                    }}
                                  >
                                    {/* Expand/collapse for nested */}
                                    {isObject || isArray ? (
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedFields((prev) => ({
                                            ...prev,
                                            [fullPath]: !isExpanded,
                                          }));
                                        }}
                                      >
                                        {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                      </IconButton>
                                    ) : depth > 0 ? (
                                      <Box sx={{ width: 40 }} />
                                    ) : null}

                                    {/* Field name and type */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          fontWeight: 600,
                                          color: "primary.main",
                                          fontSize: "0.95rem",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {key}
                                      </Typography>
                                      <Chip
                                        label={fieldType}
                                        size="small"
                                        color={
                                          fieldType === "object"
                                            ? "secondary"
                                            : fieldType === "array"
                                            ? "info"
                                            : "default"
                                        }
                                      />
                                      {fieldType === "Required" && (
                                        <Chip label="Required" size="small" color="error" />
                                      )}
                                      {(isObject || isArray) && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {isArray
                                            ? `Array items: ${fieldType} (${fieldCount} ${
                                                fieldCount === 1 ? "item" : "items"
                                              })`
                                            : `${fieldCount} nested ${
                                                fieldCount === 1 ? "field" : "fields"
                                              }`}
                                        </Typography>
                                      )}
                                      {!isObject && !isArray && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "400px",
                                          }}
                                        >
                                          {typeof value === "string" && value.length > 50
                                            ? value.substring(0, 50) + "..."
                                            : String(value)}
                                        </Typography>
                                      )}
                                    </Box>

                                    {/* Action buttons */}
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                      {depth === 0 &&
                                        (() => {
                                          const template = JSON.parse(promptTemplate);
                                          const keys = Object.keys(template);
                                          const currentIndex = keys.indexOf(key);
                                          return (
                                            <>
                                              <Tooltip title="Move Up">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    disabled={currentIndex === 0}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleMoveField(fullPath, "up");
                                                    }}
                                                    sx={{
                                                      color:
                                                        currentIndex === 0
                                                          ? "action.disabled"
                                                          : "white",
                                                    }}
                                                  >
                                                    <ArrowUpwardIcon fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                              <Tooltip title="Move Down">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    disabled={currentIndex === keys.length - 1}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleMoveField(fullPath, "down");
                                                    }}
                                                    sx={{
                                                      color:
                                                        currentIndex === keys.length - 1
                                                          ? "action.disabled"
                                                          : "white",
                                                    }}
                                                  >
                                                    <ArrowDownwardIcon fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            </>
                                          );
                                        })()}
                                      <Tooltip title="Edit Field">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenEditDialog(fullPath, key, value);
                                          }}
                                          sx={{ color: "white" }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete Field">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Delete field "${key}"?`)) {
                                              handleDeleteField(fullPath);
                                            }
                                          }}
                                          sx={{ color: "error.main" }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                </Paper>

                                {/* Render nested fields */}
                                {isExpanded && isObject && (
                                  <Box>
                                    {Object.entries(value).map(([nestedKey, nestedValue]) =>
                                      renderTemplateField(
                                        nestedKey,
                                        nestedValue,
                                        depth + 1,
                                        fullPath
                                      )
                                    )}
                                  </Box>
                                )}

                                {/* Render array items */}
                                {isExpanded && isArray && (
                                  <Box>
                                    {value.map((item, idx) =>
                                      renderTemplateField(`[${idx}]`, item, depth + 1, fullPath)
                                    )}
                                  </Box>
                                )}
                              </Box>
                            );
                          };

                          return (
                            <Box>
                              <Alert severity="info" sx={{ mb: 2 }}>
                                Visual representation of your JSON prompt template structure. Switch
                                to JSON view to edit.
                              </Alert>
                              {Object.entries(template).map(([key, value]) =>
                                renderTemplateField(key, value)
                              )}
                            </Box>
                          );
                        } catch (e) {
                          return (
                            <Alert severity="error">
                              Invalid JSON. Switch to JSON view to fix syntax errors: {e.message}
                            </Alert>
                          );
                        }
                      })()}
                    </Box>
                  )}

                  {/* JSON Editor Mode */}
                  {schemaViewMode === "json" && (
                    <Editor
                      height="100%"
                      defaultLanguage="json"
                      value={promptTemplate}
                      onChange={handlePromptChange}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: true },
                        fontSize: 13,
                        formatOnPaste: true,
                        formatOnType: true,
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                        wordWrap: "off",
                        folding: true,
                        lineNumbers: "on",
                        scrollbar: {
                          horizontal: "visible",
                          vertical: "visible",
                          horizontalScrollbarSize: 10,
                          verticalScrollbarSize: 10,
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Panel - Versions and Audit Log */}
          <Box
            sx={{
              flex: "0 0 30%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              borderLeft: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                flexShrink: 0,
                minHeight: 42,
              }}
            >
              <Tab label="Versions" sx={{ minHeight: 42 }} />
              <Tab label="Audit Log" sx={{ minHeight: 42 }} />
            </Tabs>

            <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
              {activeTab === 0 && (
                <List dense>
                  {imageSchemas.map((schema) => {
                    const isLoaded = selectedSchema?.id === schema.id;
                    return (
                      <ListItem
                        key={schema.id}
                        onClick={() => loadSchema(schema.id)}
                        sx={{
                          mb: 1,
                          border: 2,
                          borderColor: isLoaded ? "success.main" : "divider",
                          borderRadius: 1,
                          bgcolor: isLoaded ? "success.50" : "transparent",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            bgcolor: isLoaded ? "success.100" : "action.hover",
                            borderColor: isLoaded ? "success.dark" : "primary.main",
                            transform: "translateX(2px)",
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                v{schema.version}
                              </Typography>
                              {schema.is_active && (
                                <Chip label="Active" size="small" color="success" />
                              )}
                              {isLoaded && <Chip label="Loaded" size="small" color="success" />}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" display="block">
                                {schema.model} • {schema.size} • {schema.quality}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(schema.created_at).toLocaleString()} by{" "}
                                {schema.created_by}
                              </Typography>
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          {!schema.is_active && (
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Activate">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleActivateSchema(schema.id);
                                  }}
                                >
                                  <PlayArrowIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSchema(schema.id);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {activeTab === 1 && (
                <List dense>
                  {auditLog.map((log) => (
                    <ListItem
                      key={log.id}
                      sx={{ mb: 1, flexDirection: "column", alignItems: "flex-start" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Chip
                          label={log.action}
                          size="small"
                          color={
                            log.action === "ACTIVATE"
                              ? "success"
                              : log.action === "CREATE"
                              ? "primary"
                              : log.action === "DEACTIVATE"
                              ? "warning"
                              : "default"
                          }
                        />
                        <Typography variant="caption" sx={{ ml: "auto" }}>
                          {new Date(log.changed_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        v{log.version} by {log.changed_by}
                      </Typography>
                      {log.notes && (
                        <Typography variant="caption" color="text.secondary">
                          {log.notes}
                        </Typography>
                      )}
                      <Divider sx={{ width: "100%", mt: 1 }} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Edit Field Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Field: {editingFieldKey}
          <Chip label={editingFieldType} size="small" sx={{ ml: 2 }} />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {editingFieldType === "object" || editingFieldType === "array" ? (
              <Editor
                height="400px"
                defaultLanguage="json"
                value={editingFieldValue}
                onChange={(value) => setEditingFieldValue(value || "")}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  wordWrap: "on",
                  lineNumbers: "on",
                }}
              />
            ) : (
              <TextField
                fullWidth
                multiline={editingFieldType === "string"}
                rows={editingFieldType === "string" ? 6 : 1}
                label="Field Value"
                value={editingFieldValue}
                onChange={(e) => setEditingFieldValue(e.target.value)}
                type={editingFieldType === "number" ? "number" : "text"}
                helperText={`Type: ${editingFieldType}`}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEditedField} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageSchemaManagementV2;
